import { z } from "zod"

import {
  appBereichSchema,
  bereichFreigabeSchema,
  einheitFreigabeSchema,
  produktFreigabeSchema,
  trainingsProduktSchema,
} from "./einheit-freigabe"
import { trainingsEinheitSchema } from "./schemas"

/**
 * Contracts of the admin catalogue (Anforderungskatalog Admin-Bereich).
 *
 * Unlike `verwaltung.ts`, some schemas here carry names and addresses: the
 * user import and the campaign preview cannot work without them. Each of
 * those endpoints is restricted to the platform or to the manager of the own
 * Sparkasse.
 */

// ---------------------------------------------------------------------------
// Users: bulk import and transfer between tenants
// ---------------------------------------------------------------------------

/** Roles an import may assign. Admin rights are never granted in bulk. */
export const importRolleSchema = z.enum(["advisor", "coach", "manager"])

export const importZeileSchema = z.object({
  email: z.string().trim(),
  name: z.string().trim(),
  rolle: importRolleSchema.optional(),
  /** Name of an existing group of the tenant. */
  gruppe: z.string().trim().optional(),
})
export type ImportZeile = z.infer<typeof importZeileSchema>

export const importUsersSchema = z.object({
  tenantId: z.string().optional(),
  /** Checks every row and reports what would happen, without writing. */
  dryRun: z.boolean().default(true),
  zeilen: z.array(importZeileSchema).min(1).max(2000),
})
export type ImportUsersInput = z.infer<typeof importUsersSchema>

export const importErgebnisSchema = z.enum(["angelegt", "vorhanden", "fehler"])

export const importReportSchema = z.object({
  dryRun: z.boolean(),
  angelegt: z.number().int(),
  vorhanden: z.number().int(),
  fehler: z.number().int(),
  zeilen: z.array(
    z.object({
      zeile: z.number().int(),
      email: z.string(),
      ergebnis: importErgebnisSchema,
      meldung: z.string().optional(),
    }),
  ),
})
export type ImportReport = z.infer<typeof importReportSchema>

export const transferUserSchema = z.object({
  tenantId: z.string().min(1),
  teamId: z.string().optional(),
})
export type TransferUserInput = z.infer<typeof transferUserSchema>

// ---------------------------------------------------------------------------
// Mail templates and campaigns
// ---------------------------------------------------------------------------

export const mailVorlageTypSchema = z.enum([
  "anmeldelink",
  "einladung",
  "erinnerung",
  "report",
])
export type MailVorlageTyp = z.infer<typeof mailVorlageTypSchema>

export const MAIL_VORLAGE_LABELS: Record<MailVorlageTyp, string> = {
  anmeldelink: "Anmeldelink",
  einladung: "Einladung",
  erinnerung: "Erinnerung",
  report: "Nutzungsbericht",
}

/** The placeholders each template understands, written as {{name}}. */
export const MAIL_PLATZHALTER: Record<MailVorlageTyp, string[]> = {
  anmeldelink: ["name", "minuten"],
  einladung: ["name", "sparkasse"],
  erinnerung: ["name", "sparkasse"],
  report: ["zeitraum"],
}

const hexFarbeSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Farbe als Hexwert, etwa #ca2d37.")

export const mailVorlageInhaltSchema = z.object({
  betreff: z.string().trim().min(1).max(200),
  ueberschrift: z.string().trim().min(1).max(200),
  text: z.string().trim().min(1).max(5000),
  buttonText: z.string().trim().min(1).max(60),
  hinweis: z.string().trim().max(2000).default(""),
  fusszeile: z.string().trim().max(1000).default(""),
  akzentfarbe: hexFarbeSchema.default("#ca2d37"),
})
export type MailVorlageInhalt = z.infer<typeof mailVorlageInhaltSchema>

export const mailVorlageSchema = mailVorlageInhaltSchema.extend({
  typ: mailVorlageTypSchema,
  version: z.number().int(),
  /** False while the built-in default text applies. */
  angepasst: z.boolean(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
  platzhalter: z.array(z.string()),
})
export type MailVorlage = z.infer<typeof mailVorlageSchema>
export const mailVorlageListSchema = z.array(mailVorlageSchema)

export const mailVorschauSchema = z.object({
  betreff: z.string(),
  html: z.string(),
  text: z.string(),
})
export type MailVorschau = z.infer<typeof mailVorschauSchema>

export const vorlagenVersionSchema = z.object({
  version: z.number().int(),
  createdAt: z.string(),
  createdBy: z.string(),
})
export const vorlagenVersionListSchema = z.array(vorlagenVersionSchema)
export type VorlagenVersion = z.infer<typeof vorlagenVersionSchema>

export const kampagnenZielgruppeSchema = z.enum([
  "nie_angemeldet",
  "alle_aktiven",
  "alle",
])
export type KampagnenZielgruppe = z.infer<typeof kampagnenZielgruppeSchema>

export const KAMPAGNEN_ZIELGRUPPE_LABELS: Record<KampagnenZielgruppe, string> = {
  nie_angemeldet: "Eingeladen, noch nie angemeldet",
  alle_aktiven: "Alle aktiven Konten",
  alle: "Alle nicht gesperrten Konten",
}

export const kampagnenVorlageSchema = z.enum(["einladung", "erinnerung"])

export const kampagnenStatusSchema = z.enum(["geplant", "versendet", "abgebrochen"])

export const createKampagneSchema = z.object({
  tenantId: z.string().min(1),
  teamId: z.string().optional(),
  vorlage: kampagnenVorlageSchema,
  zielgruppe: kampagnenZielgruppeSchema,
  /** ISO timestamp; absent means now. */
  geplantFuer: z.iso.datetime({ offset: true }).optional(),
})
export type CreateKampagneInput = z.infer<typeof createKampagneSchema>

export const kampagneSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  teamId: z.string().optional(),
  vorlage: mailVorlageTypSchema,
  zielgruppe: kampagnenZielgruppeSchema,
  status: kampagnenStatusSchema,
  geplantFuer: z.string(),
  versendetAm: z.string().optional(),
  empfaenger: z.number().int(),
  fehler: z.number().int(),
  createdAt: z.string(),
  createdBy: z.string(),
})
export type Kampagne = z.infer<typeof kampagneSchema>
export const kampagneListSchema = z.array(kampagneSchema)

export const kampagnenVorschauSchema = z.object({
  empfaenger: z.number().int(),
})

// ---------------------------------------------------------------------------
// Documents, learning goals, content packages, release overview
// ---------------------------------------------------------------------------

/** Above this, documents belong in a file share, not in the database. */
export const UNTERLAGE_MAX_BYTES = 10 * 1024 * 1024

export const UNTERLAGE_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
] as const

export const unterlageDateiSchema = z.object({
  dateiname: z.string().trim().min(1).max(200),
  mimeType: z.enum(UNTERLAGE_MIME_TYPES),
  /** Base64 of the file, without a data-URL prefix. */
  inhaltBase64: z.string().min(1),
})

export const upsertUnterlageSchema = z.object({
  id: z.string().optional(),
  titel: z.string().trim().min(2).max(160),
  beschreibung: z.string().trim().max(1000).default(""),
  kategorie: z.string().trim().default("allgemein"),
  aktiv: z.boolean().default(true),
  /** Platform only: absent means master content for every Sparkasse. */
  tenantId: z.string().optional(),
  /** Required on create; on update it replaces the file and raises the version. */
  datei: unterlageDateiSchema.optional(),
})
export type UpsertUnterlageInput = z.infer<typeof upsertUnterlageSchema>

export const unterlageSchema = z.object({
  id: z.string(),
  titel: z.string(),
  beschreibung: z.string(),
  kategorie: z.string(),
  dateiname: z.string(),
  mimeType: z.string(),
  groesse: z.number().int(),
  version: z.number().int(),
  aktiv: z.boolean(),
  tenantId: z.string().optional(),
  updatedAt: z.string(),
  updatedBy: z.string(),
})
export type Unterlage = z.infer<typeof unterlageSchema>
export const unterlageListSchema = z.array(unterlageSchema)

export const lernzielSchema = z.object({
  id: z.string(),
  phase: z.string(),
  phaseLabel: z.string(),
  goal: z.string(),
  steps: z.array(z.string()),
  updatedAt: z.string(),
})
export type Lernziel = z.infer<typeof lernzielSchema>
export const lernzielListSchema = z.array(lernzielSchema)

export const updateLernzielSchema = z.object({
  goal: z.string().trim().min(10).max(2000),
})
export type UpdateLernzielInput = z.infer<typeof updateLernzielSchema>

export const upsertFachpaketSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(2).max(120),
  beschreibung: z.string().trim().max(1000).default(""),
  produkte: z.array(trainingsProduktSchema).default([]),
  einheiten: z.array(trainingsEinheitSchema).default([]),
  bereiche: z.array(appBereichSchema).default([]),
})
export type UpsertFachpaketInput = z.infer<typeof upsertFachpaketSchema>

export const fachpaketSchema = z.object({
  id: z.string(),
  name: z.string(),
  beschreibung: z.string(),
  produkte: z.array(trainingsProduktSchema),
  einheiten: z.array(trainingsEinheitSchema),
  bereiche: z.array(appBereichSchema),
  updatedAt: z.string(),
  updatedBy: z.string(),
})
export type Fachpaket = z.infer<typeof fachpaketSchema>
export const fachpaketListSchema = z.array(fachpaketSchema)

export const applyFachpaketSchema = z.object({ tenantId: z.string().min(1) })

/** Who has what: every customer tenant with its effective releases. */
export const freigabeUebersichtSchema = z.array(
  z.object({
    tenantId: z.string(),
    name: z.string(),
    bereiche: z.array(bereichFreigabeSchema),
    einheiten: z.array(einheitFreigabeSchema),
    produkte: z.array(produktFreigabeSchema),
  }),
)
export type FreigabeUebersicht = z.infer<typeof freigabeUebersichtSchema>
